import React from "react";
import { FolderOpen, Settings2, Bell } from "lucide-react";
import { CARD } from "@/styles/shared";
import { timeAgo } from "@/utils/timeAgo";

function ProjectCard({ project, onClick, onSettings }) {
  const isWork = project.category === "work";
  const progress = project.total_task_count > 0
    ? Math.round((project.completed_task_count / project.total_task_count) * 100)
    : 0;
  const daysSince = Math.floor((Date.now() - new Date(project.last_touched).getTime()) / 86400000);
  const isStale = daysSince > 7;

  return (
    <div
      data-testid={`project-card-${project.id}`}
      onClick={onClick}
      style={{
        background: "rgba(255,255,255,0.82)",
        border: "1px solid rgba(255,255,255,0.7)",
        borderRadius: "0.75rem",
        padding: "0.75rem",
        cursor: "pointer",
        transition: "background 0.18s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.95)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.82)")}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.35rem" }}>
        <span style={{ fontSize: "1.35rem", lineHeight: 1 }}>{project.emoji}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <span style={{
            padding: "0.1rem 0.45rem", borderRadius: "2rem", fontSize: "0.62rem", fontWeight: 700,
            fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.04em",
            background: isWork ? "rgba(61,107,31,0.12)" : "rgba(212,165,116,0.15)",
            color: isWork ? "#3d6b1f" : "#92400e",
            border: isWork ? "1px solid rgba(61,107,31,0.2)" : "1px solid rgba(212,165,116,0.35)",
          }}>
            {project.category}
          </span>
          <button
            data-testid={`project-settings-trigger-${project.id}`}
            onClick={(e) => { e.stopPropagation(); onSettings(project); }}
            title="Project settings"
            style={{
              background: "rgba(26,77,46,0.06)",
              border: "1px solid rgba(26,77,46,0.12)",
              borderRadius: "0.35rem",
              width: "22px", height: "22px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#6b7280",
              transition: "background 0.15s, color 0.15s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(26,77,46,0.14)"; e.currentTarget.style.color = "#1A4D2E"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(26,77,46,0.06)"; e.currentTarget.style.color = "#6b7280"; }}
          >
            <Settings2 size={11} />
          </button>
        </div>
      </div>

      <p style={{
        fontFamily: "'Manrope', sans-serif", fontWeight: 700,
        color: "#1A4D2E", fontSize: "0.85rem",
        margin: "0 0 0.2rem",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {project.title}
      </p>
      <p style={{
        fontFamily: "'Manrope', sans-serif",
        color: isStale ? "#d4a574" : "#6b7280",
        fontSize: "0.72rem", margin: "0 0 0.2rem",
      }}>
        {isStale ? "⚠ " : ""}Last: {timeAgo(project.last_touched)}
      </p>
      <p style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.72rem", margin: "0 0 0.45rem" }}>
        {project.active_task_count} active · {project.total_task_count} total
        {project.reminder_count > 0 && (
          <span style={{ marginLeft: "0.5rem", display: "inline-flex", alignItems: "center", gap: "0.2rem", color: "#3d6b1f", fontWeight: 600 }}>
            · <Bell size={10} color="#3d6b1f" /> {project.reminder_count}
          </span>
        )}
      </p>
      <div style={{ height: "4px", background: "rgba(26,77,46,0.1)", borderRadius: "2px", overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${progress}%`,
          background: "linear-gradient(90deg, #3d6b1f, #86c559)", borderRadius: "2px",
        }} />
      </div>
    </div>
  );
}

export default function ProjectsSection({ projects, onAdd, onSelect, onSettings }) {
  const atLimit = projects.length >= 6;
  return (
    <div data-testid="projects-section" style={{ ...CARD, background: "rgba(255,255,255,0.82)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.7)", width: "100%", maxWidth: "640px", overflow: "hidden" }}>
      <div style={{
        padding: "0.875rem 1.25rem",
        borderBottom: "1px solid rgba(26,77,46,0.12)",
        display: "flex", alignItems: "center", gap: "0.5rem",
      }}>
        <FolderOpen size={16} color="#3d6b1f" />
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 600 }}>
          Active Projects
        </span>
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#3d6b1f", fontSize: "0.85rem", fontWeight: 600 }}>
          ({projects.length}/6)
        </span>
      </div>

      <div style={{ padding: "0.75rem 1rem" }}>
        {projects.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(172px, 1fr))",
            gap: "0.625rem",
            marginBottom: "0.75rem",
          }}>
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} onClick={() => onSelect(p)} onSettings={onSettings} />
            ))}
          </div>
        )}
        <button
          data-testid="add-project-btn"
          onClick={onAdd}
          disabled={atLimit}
          style={{
            width: "100%", padding: "0.55rem",
            borderRadius: "0.625rem",
            border: atLimit ? "1px solid rgba(26,77,46,0.1)" : "1.5px dashed rgba(26,77,46,0.3)",
            background: "transparent",
            color: atLimit ? "#9ca3af" : "#3d6b1f",
            fontFamily: "'Manrope', sans-serif", fontSize: "0.85rem", fontWeight: 600,
            cursor: atLimit ? "not-allowed" : "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { if (!atLimit) e.currentTarget.style.background = "rgba(61,107,31,0.06)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          {atLimit ? "6/6 limit reached — archive a project to add more" : "+ Add New Project"}
        </button>
      </div>
    </div>
  );
}
